import { useState } from "react";
import { useTranslation } from "react-i18next";
import { twJoin, twMerge } from "tailwind-merge";

import { useCreateFriendRequest, useGetProfileSuspense, useListFriendsSuspense } from "@/api/bale";
import { ActionBox } from "@/components/ActionBox";
import { Card } from "@/components/Card";
import { Icon } from "@/components/Icon";
import { IconButton } from "@/components/IconButton";
import { Missing } from "@/components/Missing";
import { Subtitle } from "@/components/Subtitle";
import { TextContent } from "@/components/TextContent";
import { DEFAULT_PROFILE_AVATAR, profileAvatarUrl, profileDisplayName } from "@/model/profile";

export function StaticProfileCard({
  onEdit,
  accountId,
  className,
}: {
  onEdit?: () => void;
  accountId: string;
  className?: string;
}) {
  const { t } = useTranslation();
  const profile = useGetProfileSuspense(accountId, {
    query: { select: (data) => data.data.profile },
  });
  const isMe = useGetProfileSuspense("@me", {
    query: { select: (data) => data.data.profile.id === profile.data.id },
  });
  const isFriend = useListFriendsSuspense("@me", {
    query: {
      select: (data) => data.data.friends.some((friend) => friend.profile.id === profile.data.id),
    },
  });
  const createFriendRequest = useCreateFriendRequest();
  const [showingTurtle, setShowingTurtle] = useState(false);

  function addFriend() {
    createFriendRequest.mutate({ data: { friend_id: profile.data.id, note: null } });
  }

  const identity =
    profile.data.pronouns && profile.data.real_name
      ? `${profile.data.real_name} (${profile.data.pronouns})`
      : profile.data.real_name || profile.data.pronouns;

  return (
    <Card className={twMerge("isolate flex flex-row flex-wrap gap-4", className)}>
      <div className="p-4">
        <div className="relative aspect-[88_/_80] h-auto w-88 max-w-full shrink-0">
          <div
            role="presentation"
            className={twJoin(
              "absolute aspect-square h-full overflow-hidden rounded-sm bg-water shadow-lg transition duration-125",
              // oxlint-disable-next-line no-negated-condition
              !showingTurtle ? "z-10" : "left-0 z-0 translate-x-16/80 scale-80 cursor-pointer",
            )}
            onClick={showingTurtle ? () => setShowingTurtle((showing) => !showing) : undefined}
          >
            <img
              src={profileAvatarUrl(profile.data)}
              className="size-full object-contain"
              alt={profileDisplayName(profile.data)}
            />
          </div>
          <div
            role="presentation"
            className={twJoin(
              "absolute aspect-square h-full overflow-hidden rounded-sm bg-water shadow-lg transition duration-125",
              showingTurtle ? "z-10" : "left-0 z-0 translate-x-16/80 scale-80 cursor-pointer",
            )}
            onClick={showingTurtle ? undefined : () => setShowingTurtle((showing) => !showing)}
          >
            <img src={DEFAULT_PROFILE_AVATAR} className="size-full object-contain" alt="Avatar turtle" />
          </div>
        </div>
      </div>
      <div className="flex grow basis-1/3 flex-col gap-2 pb-4 pl-4">
        <div className="flex flex-row items-start justify-between">
          <div className="flex flex-col pt-4">
            <Subtitle>{profileDisplayName(profile.data)}</Subtitle>
            <div className="gap-6 p-2">
              {isMe.data && (
                <div className="flex flex-row items-center gap-1 font-hint text-placeholder">
                  <Icon height="1rem" name="check" /> {t("this-is-you")}
                </div>
              )}
              {!isMe.data && isFriend.data && (
                <div className="flex flex-row items-center gap-1 font-hint text-placeholder">
                  <Icon height="1rem" name="check" /> {t("friends")}
                </div>
              )}
              {!isMe.data && !isFriend.data && (
                <button
                  type="button"
                  className="flex cursor-pointer flex-row items-center gap-1 font-hint text-placeholder hover:text-black"
                  onClick={addFriend}
                >
                  <Icon height="1rem" name="user-plus" />{" "}
                  {createFriendRequest.isSuccess ? t("requested") : t("send-friend-request")}
                </button>
              )}
            </div>
          </div>
          <ActionBox className="p-2">
            {isMe.data && <IconButton type="button" name="edit" onClick={() => onEdit?.()} />}
          </ActionBox>
        </div>
        <div className="flex flex-col">
          {identity && (
            <p>
              <span className="font-disinterest text-placeholder">{t("profile-goes-by")}</span> {identity}
            </p>
          )}
          {profile.data.biography.raw ? (
            <p className="min-w-0">
              <span className="font-disinterest text-placeholder">{t("profile-about-me")}</span>{" "}
              <TextContent content={profile.data.biography} />
            </p>
          ) : (
            <Missing>{t("profile-biography-unfilled")}</Missing>
          )}
        </div>
      </div>
    </Card>
  );
}
